/**
 * @file Application entry: mounts React, React Query, and best-effort cleanup of
 * leftover service workers / caches. Auth session cookies and CSRF live in
 * api/client.js and AuthProvider (App).
 * @module frontend/src/index
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
// Auth cookies + CSRF are handled in api/client.js and AuthProvider inside App

// If this origin ever had a service worker (PWA/offline caching) in a past build,
// it can keep controlling clients and serve stale assets until the user clears site data.
// We don't intentionally use a service worker, so proactively unregister any that exist.
async function unregisterServiceWorkersAndClearCaches() {
  try {
    if (!('serviceWorker' in navigator)) return;

    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(regs.map((r) => r.unregister()));

    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.allSettled(keys.map((k) => window.caches.delete(k)));
    }
  } catch (_) {
    // Best-effort only; never block app boot.
  }
}

// Fire-and-forget; don't delay initial render.
unregisterServiceWorkersAndClearCaches();

// REVIEW [MEDIUM]: Broad capture-phase error / rejection suppression can hide
// real auth or CSP failures in development; keep filters narrowly scoped.
// Disable React's error overlay for development
// This must be set before React loads
if (process.env.NODE_ENV === 'development') {
  // Override React's internal error reporting mechanism
  // React uses this to show the error overlay
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  
  // Store original error handlers
  const originalOnError = window.onerror;
  const originalUnhandledRejection = window.onunhandledrejection;
  
  // Intercept all errors before React can display them
  window.addEventListener('error', function(event) {
    const errorMessage = (event.message || '').toLowerCase();
    const errorStack = (event.error?.stack || '').toLowerCase();
    const combined = errorMessage + ' ' + errorStack;
    
  const isHarmlessVideoError = 
    combined.includes('onabort') ||
    combined.includes('renderedcameraimpl') ||
    combined.includes('rendered cameraimpl') ||
    combined.includes('video surface') ||
    combined.includes('handleerror') ||
    combined.includes('cameraimpl video surface') ||
    combined.includes('rendered') && combined.includes('cameraimpl');
  
  const isHarmlessMotionError =
    combined.includes('queryselectorall') ||
    combined.includes('cannot read properties of null') ||
    combined.includes('reading \'queryselectorall\'') ||
    combined.includes('resolveelements') ||
    combined.includes('resolvesubjects') ||
    combined.includes('animatesubject') ||
    combined.includes('scopedanimate');
    
    if (isHarmlessVideoError || isHarmlessMotionError) {
      console.log('⚠️ Harmless error caught (suppressed React overlay):', event.message);
      if (event.error) {
        console.log('   Full error:', event.error);
      }
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true); // Use capture phase to catch before React
  
  // Also intercept unhandled rejections
  window.addEventListener('unhandledrejection', function(event) {
    const errorMessage = (event.reason?.message || event.reason?.toString() || '').toLowerCase();
    const errorStack = (event.reason?.stack || '').toLowerCase();
    const combined = errorMessage + ' ' + errorStack;
    
  const isHarmlessVideoError = 
    combined.includes('onabort') ||
    combined.includes('renderedcameraimpl') ||
    combined.includes('rendered cameraimpl') ||
    combined.includes('video surface') ||
    combined.includes('handleerror') ||
    combined.includes('cameraimpl video surface') ||
    combined.includes('rendered') && combined.includes('cameraimpl');
  
  const isHarmlessMotionError =
    combined.includes('queryselectorall') ||
    combined.includes('cannot read properties of null') ||
    combined.includes('reading \'queryselectorall\'') ||
    combined.includes('resolveelements') ||
    combined.includes('resolvesubjects') ||
    combined.includes('animatesubject') ||
    combined.includes('scopedanimate');
    
    if (isHarmlessVideoError || isHarmlessMotionError) {
      console.log('⚠️ Harmless error (unhandled rejection, suppressed):', event.reason);
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true); // Use capture phase
}

// Global error handler to catch and handle common video/media errors that are harmless
// These are race conditions that occur when video elements are cleaned up during React unmount
const originalError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  // Check if this is a harmless video/media error
  const errorMessage = message?.toString() || '';
  const errorStack = error?.stack?.toString() || '';
  const errorName = error?.name?.toString() || '';
  const combinedMessage = (errorMessage + ' ' + errorStack + ' ' + errorName).toLowerCase();
  
  const isHarmlessVideoError = 
    errorMessage.includes('play()') ||
    errorMessage.includes('interrupted') ||
    errorMessage.includes('removed from the document') ||
    errorMessage.includes('The play() request was interrupted') ||
    errorMessage.includes('onabort') ||
    errorMessage.includes('onabort()') ||
    errorMessage.includes('video surface') ||
    errorMessage.includes('RenderedCameraImpl') ||
    errorMessage.includes('Rendered CameraImpl') || // Note: with space
    errorMessage.includes('handleError') ||
    (errorMessage.includes('video') && errorMessage.includes('abort')) ||
    combinedMessage.includes('onabort') ||
    combinedMessage.includes('renderedcameraimpl') ||
    combinedMessage.includes('rendered cameraimpl') || // Note: with space
    combinedMessage.includes('video surface') ||
    combinedMessage.includes('handleerror') ||
    combinedMessage.includes('cameraimpl video surface') ||
    (errorMessage.includes('rendered') && errorMessage.includes('cameraimpl')) ||
    source?.includes('bundle.js') && combinedMessage.includes('onabort');

  const isHarmlessMotionError =
    errorMessage.includes('querySelectorAll') ||
    errorMessage.includes('Cannot read properties of null') ||
    errorMessage.includes('reading \'querySelectorAll\'') ||
    combinedMessage.includes('queryselectorall') ||
    combinedMessage.includes('cannot read properties of null') ||
    combinedMessage.includes('reading \'queryselectorall\'') ||
    combinedMessage.includes('resolveelements') ||
    combinedMessage.includes('resolvesubjects') ||
    combinedMessage.includes('animatesubject') ||
    combinedMessage.includes('scopedanimate');

  if (isHarmlessVideoError || isHarmlessMotionError) {
    // Log to console and suppress React overlay
    console.log('⚠️ Harmless video error caught by window.onerror (suppressed overlay):', errorMessage);
    if (error) {
      console.log('   Error object:', error);
      console.log('   Stack:', error.stack);
    }
    return true; // Prevent default error handling
  }

  // Also suppress WebSocket errors (harmless - just means hot reload won't work)
  if (errorMessage.includes('WebSocket') || errorMessage.includes('wss://')) {
    console.debug('Suppressed WebSocket error (hot reload not available):', errorMessage);
    return true; // Prevent default error handling
  }

  // Call original error handler for other errors
  if (originalError) {
    return originalError(message, source, lineno, colno, error);
  }
  return false;
};

// Also catch unhandled promise rejections for the same issue
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason) {
    const errorMessage = typeof event.reason === 'string' 
      ? event.reason 
      : (event.reason?.message || '');
    
    const isHarmlessVideoError = 
      errorMessage.includes('play()') || 
      errorMessage.includes('interrupted') || 
      errorMessage.includes('removed from the document') ||
      errorMessage.includes('The play() request was interrupted') ||
      errorMessage.includes('onabort') ||
      errorMessage.includes('onabort()') ||
      errorMessage.includes('video surface') ||
      errorMessage.includes('RenderedCameraImpl') ||
      errorMessage.includes('handleError') ||
      (errorMessage.includes('video') && errorMessage.includes('abort'));

    const isHarmlessMotionError =
      errorMessage.includes('querySelectorAll') ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes('reading \'querySelectorAll\'') ||
      errorMessage.includes('resolveElements') ||
      errorMessage.includes('resolveSubjects') ||
      errorMessage.includes('animateSubject') ||
      errorMessage.includes('scopedAnimate');

    if (isHarmlessVideoError || isHarmlessMotionError) {
      // Suppress the error
      console.debug('Suppressed harmless video promise rejection:', errorMessage);
      event.preventDefault(); // Prevent error from being logged/shown
      return;
    }

    // Also suppress WebSocket errors
    if (errorMessage.includes('WebSocket') || errorMessage.includes('wss://')) {
      console.debug('Suppressed WebSocket promise rejection (hot reload not available):', errorMessage);
      event.preventDefault();
      return;
    }
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Override React's error reporting - log to console but suppress overlay for harmless video errors
if (process.env.NODE_ENV === 'development') {
const originalConsoleError = console.error;
console.error = function(...args) {
  // Check if this is a harmless video error
  const errorString = args.map(arg => {
    if (typeof arg === 'string') return arg;
    if (arg?.message) return arg.message;
    if (arg?.stack) return arg.stack;
    if (arg?.toString) return arg.toString();
    return String(arg);
  }).join(' ').toLowerCase();
  
  const isHarmlessVideoError = 
    errorString.includes('onabort') ||
    errorString.includes('renderedcameraimpl') ||
    errorString.includes('rendered cameraimpl') || // Note: with space
    errorString.includes('video surface') ||
    errorString.includes('handleerror') ||
    errorString.includes('video surface onabort') ||
    errorString.includes('cameraimpl video surface') ||
    (errorString.includes('rendered') && errorString.includes('cameraimpl'));
  
  const isHarmlessMotionError =
    errorString.includes('queryselectorall') ||
    errorString.includes('cannot read properties of null') ||
    errorString.includes('reading \'queryselectorall\'') ||
    errorString.includes('resolveelements') ||
    errorString.includes('resolvesubjects') ||
    errorString.includes('animatesubject') ||
    errorString.includes('scopedanimate');
  
  if (isHarmlessVideoError || isHarmlessMotionError) {
    // Log to console (no overlay) - user can see it in console
    // Use console.log instead of console.debug so it's more visible
    console.log('⚠️ Harmless video error (suppressed overlay, console only):', ...args);
    // Also log the full error details for debugging
    if (args.length > 0) {
      console.log('   Error details:', args);
    }
    return; // Don't call original console.error to prevent React overlay
  }
  
  // For other errors, log to console (they'll still show in console)
  // React overlay will still show for these, but you'll see them in console too
  originalConsoleError.apply(console, args);
};

// Also override console.warn to catch any warnings about this
const originalConsoleWarn = console.warn;
console.warn = function(...args) {
  const warnString = args.map(arg => {
    if (typeof arg === 'string') return arg;
    if (arg?.message) return arg.message;
    if (arg?.stack) return arg.stack;
    return String(arg);
  }).join(' ').toLowerCase();
  
  if (warnString.includes('onabort') || 
      warnString.includes('renderedcameraimpl') || 
      warnString.includes('video surface') ||
      warnString.includes('queryselectorall') ||
      warnString.includes('cannot read properties of null') ||
      warnString.includes('resolveelements')) {
    return; // Suppress
  }
  
  originalConsoleWarn.apply(console, args);
};
}

// Disable React's error overlay entirely in development
// This prevents the overlay popup from appearing - errors will only show in console
if (process.env.NODE_ENV === 'development') {
  // Override the error overlay by intercepting React's error reporting
  const originalReportError = window.reportError;
  if (window.reportError) {
    window.reportError = function(error) {
      const errorMessage = error?.message || error?.toString() || '';
      const errorStack = error?.stack || '';
      const combined = (errorMessage + ' ' + errorStack).toLowerCase();
      
  const isHarmlessVideoError = 
    combined.includes('onabort') ||
    combined.includes('renderedcameraimpl') ||
    combined.includes('rendered cameraimpl') ||
    combined.includes('video surface') ||
    combined.includes('handleerror') ||
    combined.includes('cameraimpl video surface') ||
    combined.includes('rendered') && combined.includes('cameraimpl');
  
  const isHarmlessMotionError =
    combined.includes('queryselectorall') ||
    combined.includes('cannot read properties of null') ||
    combined.includes('reading \'queryselectorall\'') ||
    combined.includes('resolveelements') ||
    combined.includes('resolvesubjects') ||
    combined.includes('animatesubject') ||
    combined.includes('scopedanimate');
      
      if (isHarmlessVideoError || isHarmlessMotionError) {
        console.log('⚠️ Harmless video error caught by reportError (suppressed overlay):', error);
        return; // Don't report to React overlay
      }
      
      // For other errors, log to console
      console.error('❌ Error reported (console only, no overlay):', error);
      if (originalReportError) {
        originalReportError.call(window, error);
      }
    };
  }
  
  // Also try to disable React's error overlay by setting the error handler
  // React uses this internally for its error overlay
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (hook.onCommitFiberRoot) {
      const originalOnCommitFiberRoot = hook.onCommitFiberRoot;
      hook.onCommitFiberRoot = function(...args) {
        // Let React DevTools work normally, but we'll catch errors elsewhere
        return originalOnCommitFiberRoot.apply(this, args);
      };
    }
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);





